import UnAuthorizedLayout from "../../../../branchAndBrandSpecific/layout/UnAuthorizedLayout";
import { FC, useEffect, useState } from "react";
import { AuthCard } from "../components/AuthCard";
import { Box, Separator, Spinner } from "@radix-ui/themes";
import {
  AuthenticationProps,
  AuthenticationResponse,
} from "../types/authenticationTypes";
// import { brand } from "../../../../branchAndBrandSpecific/utils/brandVariables/brandVariables";
import { brand } from "branchAndBrandSpecific/utils/brandVariables/brandVariables";
import useCloudFunctionCall from "@/hooks/useCloudFunctionCall";
import { createToast } from "@/utils/toaster/createToast";
import { useNavigate } from "react-router-dom";
import { auth } from "@/api/firebase/firebase.config";
import { FirebaseAuthMethods } from "../components/FirebaseAuthMethods";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import i18next from "i18next";
import { useAtomValue, useSetAtom } from "jotai";
import { authCredentialsAtom } from "@/utils/atoms/atomClient";
import { storageToRemove } from "@/utils/constants/localStorageKeys";
import { Button, Flex, TextField, Text } from "@radix-ui/themes";
import { collectionDoc } from "@/api/collectionRef/collectionOperations";

export const Login: FC<{}> = () => {
  const atomCredentials = useAtomValue(authCredentialsAtom);
  const setAtomCredentials = useSetAtom(authCredentialsAtom);
  const [userCredentials, setUserCredentials] = useState<{
    email: string;
    password: string;
  }>(
    atomCredentials || {
      email: "",
      password: "",
    }
  );
  const [googleAuthLoading, setGoogleAuthLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const baseAuthenticationProps = {
    method: "email",
    emailAddress: userCredentials.email,
    platform: brand.brandValue,
    tenantId: brand.tenantId,
    // platform: "jbl",
  } as AuthenticationProps;

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userCredentials.email);
  const setFieldValue = async (targetName: string, value: any) => {
    return setUserCredentials((prev: any) => ({
      ...prev,
      [targetName]: value,
    }));
  };

  // Auth logic
  const [response, _loading, _error, authenticate] =
    useCloudFunctionCall<AuthenticationResponse>(
      "userAuthentication",
      undefined
    );

  useEffect(() => {
    if (userCredentials.email) {
      if (response.status === "valid-email-address") {
        // Try to log in with a old account first
        auth
          .signInWithEmailAndPassword(response.email, userCredentials.password)
          .then(async (res) => {
            const profileRef = await collectionDoc.profiles(
              res.user.uid,
              brand.tenantId
            );

            if (res.user && res.user.uid) {
              navigate("/home");
              setAtomCredentials({
                email: "",
                password: "",
              });
            }
            return setGoogleAuthLoading(false);
          })
          .catch(async (err) => {
            // if user tries to login with gmail account or wrong password
            if (err.code === "auth/wrong-password") {
              return createToast({
                type: "error",
                message: i18next.t("auth.toast.password.wrong"),
              });
            }
          });
      }

      if (response.status === "valid-credentials") {
        createToast({ type: "success", message: "Login successfull" });
        async () => {
          await auth
            .signInWithCustomToken(response.token)
            .then((res) => {
              if (res.user && res.user.uid) {
                setAtomCredentials({
                  email: "",
                  password: "",
                });
                // Remove any local data from localstorage
                storageToRemove.map((storageItem) => {
                  localStorage.removeItem(storageItem);
                });

                if (res.user) {
                  // navigate("/dashboard");
                }
              }

              return setGoogleAuthLoading(false);
            })
            .catch(async (err) => {
              // if user tries to login with gmail account or wrong password
              if (err.code === "auth/wrong-password") {
                return createToast({
                  type: "error",
                  message: i18next.t("auth.toast.password.wrong"),
                });
              }
            });
        };

        localStorage.removeItem("authEmailLocal");
        localStorage.removeItem("signUpMethod");
        return navigate("/dashboard");
      }

      if (response.status === "email-not-verified") {
        localStorage.setItem(
          "authEmailLocal",
          response.email || userCredentials.email
        );
        authenticate({
          ...baseAuthenticationProps,
          action: "resent-verification-code",
        });
        return navigate("/verify-email");
      }

      if (response.status === "missing-username") {
        localStorage.setItem("authEmailLocal", userCredentials.email);

        createToast({
          type: "error",
          message: i18next.t("auth.toast.profile.incomplete"),
        });
        navigate(response.redirectURL);
        return;
      }

      if (response.status !== "email-verification-sent") {
        return createToast({ type: "error", message: response.payload });
      }

      if (response.status === "email-verification-sent") {
        return createToast({ type: "success", message: response.payload });
      }
    }
  }, [response]);

  const handleNextStep = async () => {
    await authenticate({
      ...baseAuthenticationProps,
      action: "validate-email",
    });
  };

  useEffect(() => {
    if (atomCredentials.email && atomCredentials.password) {
      setUserCredentials(atomCredentials);
      handleNextStep();
    }
  }, []);
  // Page Document Title
  useDocumentTitle(i18next.t("auth.login.headline"));

  return (
    <UnAuthorizedLayout>
      <AuthCard title={i18next.t("auth.login.headline")} type="login">
        <Flex direction="column" gap="4">
          {/* Email */}
          <Flex direction="column" gap="1">
            <Text as="label" size="2">
              {i18next.t("auth.input.username.label")}
            </Text>
            <TextField.Root
              placeholder={i18next.t("auth.input.username.placeholder")}
              size="3"
              type="text"
              value={userCredentials?.email}
              onChange={(e) => {
                setFieldValue("email", e.target.value);
              }}
              onKeyDown={async (e) => {
                if (e.key === "Enter") {
                  if (userCredentials?.password) {
                    handleNextStep();
                  }
                }
              }}
            />
          </Flex>
          {/* Password */}
          <Flex direction="column" gap="1">
            <Flex justify="between" align="end">
              <Text as="label" size="2">
                {i18next.t("auth.input.password.label")}
              </Text>
              <Text
                size="1"
                color="gray"
                onClick={() => navigate("/forgot-password")}
              >
                {i18next.t("auth.login.forgot_password")}
              </Text>
            </Flex>
            <TextField.Root
              placeholder={i18next.t("auth.input.password.placeholder")}
              size="3"
              type="password"
              value={userCredentials?.password}
              onChange={(e) => {
                setFieldValue("password", e.target.value);
              }}
              onKeyDown={async (e) => {
                if (e.key === "Enter") {
                  if (userCredentials?.password) {
                    handleNextStep();
                  }
                }
              }}
            />
          </Flex>

          {/* Button */}
          <Button
            disabled={!userCredentials.password || googleAuthLoading}
            variant="solid"
            size="3"
            onClick={async (e) => {
              handleNextStep();
            }}
          >
            {_loading ? <Spinner /> : i18next.t("auth.input.button.login")}
          </Button>
        </Flex>

        {(brand.hasAuth.bnet ||
          brand.hasAuth.google ||
          brand.hasAuth.discord ||
          brand.hasAuth.quickSignUp ||
          brand.hasAuth.twitch ||
          brand.hasAuth.steam) && (
          <>
            <Box py="5">
              <Separator
                className="SeparatorRoot"
                decorative
                style={{ width: "100%" }}
              />
            </Box>
            <FirebaseAuthMethods email={userCredentials.email} />
          </>
        )}
      </AuthCard>
    </UnAuthorizedLayout>
  );
};
